import { CdkDragMove } from '@angular/cdk/drag-drop';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef, HostListener,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { AutoFocusDirective } from '../../../actions/@base/directives/auto-focus.directive';
import { UtilitiesService } from '../../../actions/@base/services';
import { CSS_SIZE_UNIT_PX } from "../../../../external/models/css-consts";
import { AbstractDrawerTextComponent, FontSize } from "./abstract-drawer-text.component";
import { DrawerTextUtil } from "../../../utils/drawer-text.util";
import { filter, skip } from "rxjs/operators";

@Component({
  selector: 'txtb-drawer-text',
  templateUrl: './drawer-text.component.html',
  styleUrls: ['./drawer-text.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class DrawerTextComponent extends AbstractDrawerTextComponent implements OnDestroy, AfterViewInit {
  @ViewChild(AutoFocusDirective) textAreaElementFocus!: AutoFocusDirective;
  @ViewChild('textContentElement') textContent!: ElementRef;
  @ViewChild('dragContainer') dragContainer!: ElementRef;

  @HostListener('mousedown', ['$event']) onMousedown(event: MouseEvent|TouchEvent): void {
    event.stopPropagation();
  }

  @HostListener('touchstart', ['$event']) onTouchstart(event: MouseEvent|TouchEvent): void {
    event.stopPropagation();
  }

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent|TouchEvent): void {
    event.stopPropagation();
    const target = event.target as HTMLInputElement;
    this.caretIndex = this.utilitiesService.getCaretIndex(target);
  }


  isChanged = false;
  isConnected = true;
  readonly saving$ubject = new Subject<void>();
  private readonly subscription = new Subscription();
  private inputText = '';

  constructor(
    public elementRef: ElementRef,
    protected changeDetector: ChangeDetectorRef,
    protected utilitiesService: UtilitiesService
  ) {
    super(elementRef, changeDetector, utilitiesService);
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.inputText = this.content;
    this.transformBoxContentPosition();
    this.elementRef.nativeElement.style.fontSize = `${ this.fontSize }${ CSS_SIZE_UNIT_PX }`;

    this.subscription.add(this.isOnline$.subscribe((online) => (this.isConnected = online)));

    if (!this.isConnected) {
      this.toggleActiveBox(false);
      this.content.length === 0 && this.removeNote.emit(this.noteId);
      return;
    }

    this.subscription.add(
      this.currentClickedTarget$.pipe(skip(1)).subscribe((target) => {
        if (
          !this.elementRef.nativeElement.contains(target) &&
          this.textContent.nativeElement.innerText.length === 0
        ) {
          this.removeNote.emit(this.noteId);
        }
      })
    );

    this.subscription.add(
      this.saving$ubject.pipe(filter(() => !!this.noteId && this.isChanged)).subscribe(() => {
        if (!this.textColor) {
          return;
        }
        this.emitNoteChanges();
        this.isChanged = false;
      })
    );
  }

  onDragStart(): void {
    this.isDragging = true;
    this.elementRef.nativeElement.style.left = this.x;
    this.elementRef.nativeElement.style.top = this.y;
  }

  onDragEnd(): void {
    this.isDragging = false;
    this.isConnected && this.contentIsValid && this.saving$ubject.next();
  }

  onDragMove(event: CdkDragMove): void {
    this.toggleActiveBox(true);
    this.isChanged = true;

    const dragContainerRect = this.dragContainer?.nativeElement.getBoundingClientRect();
    const parentElementRect = this.wrapper.getBoundingClientRect();
    const position = DrawerTextUtil.calculatePosition(event, dragContainerRect, parentElementRect);
    const xPos = this.utilitiesService.percentage(position.x, parentElementRect.width);
    const yPos = this.utilitiesService.percentage(position.y, parentElementRect.height);
    this.x = `${ xPos }`;
    this.y = `${ yPos }`;
    this.changeDetector.markForCheck();

    this.transformBoxContentPosition();
  }

  changeFontSize(diff: number): void {
    const newFontSize = this.fontSize + diff;

    if (newFontSize <= FontSize.MINIMUM || newFontSize >= FontSize.MAXIMUM) {
      return;
    }

    this.fontSize = newFontSize;
    this.elementRef.nativeElement.style.fontSize = `${ this.fontSize }${ CSS_SIZE_UNIT_PX }`;
    this.fontSizeChange.emit(this.fontSize);
    this.changeDetector.markForCheck();
  }

  remove(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.removeNote.emit(this.noteId);
    this.resetBoxPosition();
  }

  onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.inputText = target.innerText;
  }

  onFocus(): void {
    if (!this.isConnected || this.disabled) {
      return;
    }
    this.canvasEdited.emit(true);
    this.isTextToolActive.emit(true);
    this.toggleActiveBox(true);
    this.onHideUnClickedNotes(this.noteId);

    this.supportsTouchDevices &&
    this.isVirtualKeyboardSupported &&
    this.onVirtualKeyboardGeometryChange();
  }

  onBlur(): void {
    if (!this.isConnected) {
      return;
    }

    this.textContentChange.emit(this.inputText);

    this.resetBoxPosition();
  }

  onClickOutside(): void {
    this.isConnected && this.textContentChange.emit(this.inputText);

    if (this.isConnected && this.contentIsValid) {
      this.isChanged = this.isBoxActive;
      this.saving$ubject.next();
      this.resetBoxPosition();
    }
    this.toggleActiveBox(false);
  }

  onHideUnClickedNotes(noteId = ''): void {
    this.hideUnClickedNotes.emit(noteId);
  }

  onVirtualKeyboardGeometryChange(): void {
    if ('virtualKeyboard' in navigator) {
      const virtualKeyboard = (navigator as unknown as { virtualKeyboard: HTMLElement })
        .virtualKeyboard;
      virtualKeyboard.addEventListener('geometrychange', (e: any) =>
        DrawerTextUtil.handleGeometryChange(
          e.target.boundingRect,
          this.elementRef.nativeElement,
          this.isBoxActive,
          this.utilitiesService
        )
      );
    }
  }

  onInputKeypress(event: KeyboardEvent): void {
    this.transformBoxContentPosition();
    !DrawerTextUtil.isAllowed(event, this.maxLengthText) && event.preventDefault();
  }

  resetBoxPosition(): void {
    this.elementRef.nativeElement.style.top = this.y;
  }

  ngOnDestroy(): void {
    this.isActive.emit(false);
    this.subscription.unsubscribe();
  }
}
